import React, { useState, useEffect, useCallback } from 'react';
import { workshopApi, depotsApi } from '../services/api';
import { useSettings } from '../context/SettingsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Loader2, 
  Wrench, 
  AlertTriangle, 
  Clock, 
  Calendar,
  User,
  Package,
  RefreshCw,
  Play,
  CheckCircle,
  AlertCircle,
  MapPin
} from 'lucide-react';

// Format seconds to HH:MM:SS
const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Work Order Card Component
const WorkOrderCard = ({ workOrder, variant = 'default' }) => {
  const { formatDate } = useSettings();
  const bgColor = {
    overdue: 'bg-red-900/20 border-red-500/50',
    in_progress: 'bg-blue-900/20 border-blue-500/50',
    upcoming: 'bg-green-900/20 border-green-500/30',
    default: 'bg-gray-800/50 border-gray-700'
  }[variant];

  const priorityColors = {
    critical: 'bg-red-500 text-white',
    high: 'bg-yellow-500 text-black',
    normal: 'bg-gray-600 text-white'
  };

  return (
    <div className={`p-3 rounded-lg border ${bgColor} transition-all`}>
      <div className="flex items-start justify-between mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-sm font-bold text-white truncate">{workOrder.title}</h3>
            {workOrder.priority !== 'normal' && (
              <Badge className={`${priorityColors[workOrder.priority]} text-xs px-1.5 py-0`}>
                {workOrder.priority.toUpperCase()}
              </Badge>
            )}
          </div>
        </div>
        {workOrder.timer_is_running && (
          <div className="flex items-center gap-1 text-green-400 animate-pulse ml-2">
            <Play className="w-3 h-3 fill-current" />
            <span className="text-xs font-mono">ACTIVE</span>
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-2 text-xs text-gray-300 mb-2">
        <Package className="w-3 h-3 flex-shrink-0" />
        <span className="truncate">{workOrder.asset_name}</span>
        <span className="text-gray-500">•</span>
        <span className="text-gray-400 truncate">{workOrder.asset_serial}</span>
      </div>

      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1 text-gray-400">
          <Calendar className="w-3 h-3" />
          <span>{formatDate(workOrder.scheduled_date)}</span>
        </div>
        {workOrder.assigned_name ? (
          <div className="flex items-center gap-1 text-blue-400">
            <User className="w-3 h-3" />
            <span className="truncate max-w-[80px]">{workOrder.assigned_name}</span>
          </div>
        ) : (
          <span className="text-gray-500 text-xs">Unassigned</span>
        )}
        {workOrder.total_time_seconds > 0 && (
          <div className="flex items-center gap-1 text-gray-400">
            <Clock className="w-3 h-3" />
            <span className="font-mono">{formatTime(workOrder.total_time_seconds)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Defect Card Component
const DefectCard = ({ defect }) => {
  const severityColors = {
    critical: 'bg-red-500 text-white',
    high: 'bg-yellow-500 text-black',
    medium: 'bg-orange-500 text-white',
    low: 'bg-gray-500 text-white'
  };

  return (
    <div className="p-3 rounded-lg bg-gray-800/50 border border-gray-700">
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
          <h4 className="font-semibold text-white text-sm truncate">{defect.title}</h4>
        </div>
        <Badge className={`${severityColors[defect.severity]} text-xs px-1.5 py-0 ml-2`}>
          {defect.severity.toUpperCase()}
        </Badge>
      </div>
      <div className="flex items-center gap-2 text-xs text-gray-400 mt-1">
        <Package className="w-3 h-3 flex-shrink-0" />
        <span className="truncate">{defect.asset_name}</span>
        {defect.assigned_name && (
          <>
            <span className="text-gray-600">•</span>
            <User className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{defect.assigned_name}</span>
          </>
        )}
      </div>
    </div>
  );
};

// Workshop Auth Screen
const WorkshopAuth = ({ onAuth }) => {
  const [clientId, setClientId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientId.trim()) {
      setError('Please enter a Client ID');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await workshopApi.auth(clientId.trim());
      localStorage.setItem('workshop_token', response.data.token);
      localStorage.setItem('workshop_company', response.data.company_name);
      onAuth(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid Client ID');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-gray-900 border-gray-800">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-yellow-500/20 rounded-xl flex items-center justify-center mb-4">
            <Wrench className="w-8 h-8 text-yellow-500" />
          </div>
          <CardTitle className="text-2xl text-white">Workshop Command Center</CardTitle>
          <p className="text-gray-400 mt-2">Enter your Client ID to connect</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Client ID</Label>
              <Input
                value={clientId}
                onChange={(e) => setClientId(e.target.value.toUpperCase())}
                placeholder="E.G. EQG001"
                className="text-center text-xl tracking-widest bg-gray-800 border-gray-700 text-white"
                autoFocus
              />
            </div>
            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}
            <Button 
              type="submit" 
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
              Connect Workshop
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Workshop Dashboard
const WorkshopDashboard = ({ companyName, onLogout }) => {
  const [data, setData] = useState(null);
  const [depots, setDepots] = useState([]);
  const [selectedDepot, setSelectedDepot] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);

  // Load depots on mount
  useEffect(() => {
    const loadDepots = async () => {
      try {
        const token = localStorage.getItem('workshop_token');
        if (!token) return;
        const response = await workshopApi.getDepots(token);
        setDepots(response.data || []);
      } catch (err) {
        console.error('Failed to load depots:', err);
      }
    };
    loadDepots();
  }, []);

  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem('workshop_token');
      if (!token) {
        onLogout();
        return;
      }
      const depotId = selectedDepot === 'all' ? null : selectedDepot;
      const response = await workshopApi.getDashboard(token, depotId);
      setData(response.data);
      setLastUpdate(new Date());
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('workshop_token');
        localStorage.removeItem('workshop_company');
        onLogout();
      } else {
        setError('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout, selectedDepot]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-yellow-500" />
      </div>
    );
  }

  const currentTime = new Date().toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  return (
    <div className="min-h-screen bg-gray-950 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
            <Wrench className="w-6 h-6 text-yellow-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">{companyName}</h1>
            <p className="text-gray-400">Workshop Command Center</p>
          </div>
        </div>
        
        {/* Depot Selector */}
        {depots.length > 0 && (
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-yellow-500" />
            <Select value={selectedDepot} onValueChange={setSelectedDepot}>
              <SelectTrigger className="w-56 bg-gray-800 border-gray-700 text-white" data-testid="workshop-depot-select">
                <SelectValue placeholder="Select Depot" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-700">
                <SelectItem value="all" className="text-white hover:bg-gray-700">
                  All Depots
                </SelectItem>
                {depots.map(depot => (
                  <SelectItem key={depot.id} value={depot.id} className="text-white hover:bg-gray-700">
                    {depot.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-4xl font-bold text-white font-mono">{currentTime}</p>
            {lastUpdate && (
              <p className="text-xs text-gray-500">
                Updated {lastUpdate.toLocaleTimeString()}
              </p>
            )}
          </div>
          <Button 
            variant="outline" 
            size="icon"
            onClick={loadData}
            className="text-gray-400 hover:text-white"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-900/20 border border-red-500/50 rounded-lg text-red-400">
          {error}
        </div>
      )}

      {/* Main Grid - 4 Column Layout for TV Display */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Column 1: Overdue Work Orders */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-bold text-red-400">
              OVERDUE ({data?.overdue_work_orders?.length || 0})
            </h2>
          </div>
          <div className="space-y-2 max-h-[calc(100vh-180px)] overflow-y-auto pr-1 scrollbar-thin">
            {data?.overdue_work_orders?.length > 0 ? (
              data.overdue_work_orders.map(wo => (
                <WorkOrderCard key={wo.id} workOrder={wo} variant="overdue" />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No overdue work orders</p>
              </div>
            )}
          </div>
        </div>

        {/* Column 2: In Progress Work Orders */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Play className="w-5 h-5 text-blue-500" />
            <h2 className="text-lg font-bold text-blue-400">
              IN PROGRESS ({data?.in_progress_work_orders?.length || 0})
            </h2>
          </div>
          <div className="space-y-2 max-h-[calc(100vh-180px)] overflow-y-auto pr-1 scrollbar-thin">
            {data?.in_progress_work_orders?.length > 0 ? (
              data.in_progress_work_orders.map(wo => (
                <WorkOrderCard key={wo.id} workOrder={wo} variant="in_progress" />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No work in progress</p>
              </div>
            )}
          </div>
        </div>

        {/* Column 3: Upcoming Work Orders */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-5 h-5 text-green-500" />
            <h2 className="text-lg font-bold text-green-400">
              UPCOMING ({data?.upcoming_work_orders?.length || 0})
            </h2>
          </div>
          <div className="space-y-2 max-h-[calc(100vh-180px)] overflow-y-auto pr-1 scrollbar-thin">
            {data?.upcoming_work_orders?.length > 0 ? (
              data.upcoming_work_orders.map(wo => (
                <WorkOrderCard key={wo.id} workOrder={wo} variant="upcoming" />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No upcoming work orders</p>
              </div>
            )}
          </div>
        </div>

        {/* Column 4: Incoming Defects */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <h2 className="text-lg font-bold text-yellow-400">
              DEFECTS ({data?.incoming_defects?.length || 0})
            </h2>
          </div>
          <div className="space-y-2 max-h-[calc(100vh-180px)] overflow-y-auto pr-1 scrollbar-thin">
            {data?.incoming_defects?.length > 0 ? (
              data.incoming_defects.map(defect => (
                <DefectCard key={defect.id} defect={defect} />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No incoming defects</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-4 left-4">
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => {
            localStorage.removeItem('workshop_token');
            localStorage.removeItem('workshop_company');
            onLogout();
          }}
          className="text-gray-600 hover:text-gray-400"
        >
          Disconnect
        </Button>
      </div>
    </div>
  );
};

// Main Export
export const WorkshopPage = () => {
  const [auth, setAuth] = useState(null);

  useEffect(() => {
    // Check for existing workshop session
    const token = localStorage.getItem('workshop_token');
    const company = localStorage.getItem('workshop_company');
    if (token && company) {
      setAuth({ token, company_name: company });
    }
  }, []);

  if (!auth) {
    return <WorkshopAuth onAuth={setAuth} />;
  }

  return (
    <WorkshopDashboard 
      companyName={auth.company_name}
      onLogout={() => setAuth(null)}
    />
  );
};

export default WorkshopPage;
